/**
 * Locked map-pin state. Three hooks over one global (localStorage-persisted)
 * atom (pick by intent):
 *  - `useLockedMapPin()` - map-scoped reader; the DEFAULT for consumers.
 *  - `useLockMapPinActions()` - lock/unlock controls (pin-drawer toggle).
 *  - `useLockedMapPinState()` - internal raw state the other two build on.
 */
import { useCallback, useEffect } from 'react';
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
} from 'recoil';
import { LatLng } from 'shared/types';
import { localStorageEffect } from 'libs/recoil';
import { useRecenterMap } from 'features/mapping/hooks/useCenterMap';
import { LOCAL_STORAGE_KEY } from 'state/localStorage';
import { useMapPinEnabled, useSetMapPin } from './useMapPin';
import { useIsMapPinActive } from './useIsMapPinActive';
import { useLatLngFromUrl } from './useLatLngFromUrl';
import { COORDINATE_DISPLAY_FRACTION_DIGITS } from '../../../constants';

export type LockedMapPinState = {
  lockedPin: LatLng | null;
  isLocked: boolean;
  lockPin: (coords: LatLng) => void;
  unlockPin: () => void;
  goToLockedPin: () => void;
  recenterOnLockedPin: () => void;
};

/** Map-scoped, read-only view of the locked pin (see `useLockedMapPin`). */
export type LockedMapPin = Pick<
  LockedMapPinState,
  'lockedPin' | 'isLocked' | 'goToLockedPin' | 'recenterOnLockedPin'
>;

/** Lock/unlock controls for the pin-drawer toggle (see `useLockMapPinActions`). */
export type LockMapPinActions = Pick<
  LockedMapPinState,
  'isLocked' | 'lockPin' | 'unlockPin'
>;

export const lockedMapPinAtom = atom<LatLng | null>({
  key: LOCAL_STORAGE_KEY.LOCKED_MAP_PIN,
  default: null,
  effects: [localStorageEffect(LOCAL_STORAGE_KEY.LOCKED_MAP_PIN)],
});

/**
 * Lock/unlock controls for the pin-drawer toggle. Reads the GLOBAL state on
 * purpose: the drawer renders outside `LockedMapPinActiveProvider`, where a
 * scoped read would always be `false`.
 */
export const useLockMapPinActions = (): LockMapPinActions => {
  const [lockedPin, setLockedPin] = useRecoilState(lockedMapPinAtom);
  const resetLockedPin = useResetRecoilState(lockedMapPinAtom);

  const lockPin = useCallback(
    (coords: LatLng): void => {
      setLockedPin({ lat: coords.lat, lng: coords.lng });
    },
    [setLockedPin],
  );

  const unlockPin = useCallback((): void => {
    resetLockedPin();
  }, [resetLockedPin]);

  return { isLocked: lockedPin !== null, lockPin, unlockPin };
};

/** INTERNAL raw state - prefer `useLockedMapPin` or `useLockMapPinActions`. */
export const useLockedMapPinState = (): LockedMapPinState => {
  const lockedPin = useRecoilValue(lockedMapPinAtom);
  const recenterMap = useRecenterMap();
  const showMapPin = useMapPinEnabled();
  const { setSelectedMapPin } = useSetMapPin();
  const { isLocked, lockPin, unlockPin } = useLockMapPinActions();

  const centerOnLockedPin = useCallback((): void => {
    if (!lockedPin) {
      return;
    }
    recenterMap(lockedPin, {
      drawerIsOpen: showMapPin,
      defaultSnapPointPct: 0.47,
    });
  }, [lockedPin, recenterMap, showMapPin]);

  const goToLockedPin = useCallback((): void => {
    if (!lockedPin) {
      return;
    }
    // Stay on the current map rather than always jumping to the main map.
    setSelectedMapPin(lockedPin);
    centerOnLockedPin();
  }, [lockedPin, setSelectedMapPin, centerOnLockedPin]);

  return {
    lockedPin,
    isLocked,
    lockPin,
    unlockPin,
    goToLockedPin,
    recenterOnLockedPin: centerOnLockedPin,
  };
};

/**
 * While a pin is locked, treat it as authoritative: if a pin URL points at
 * different coordinates (e.g. a shared `/@lat,lng/pin` link), redirect to the
 * locked pin so the marker, centering, and drawer stay consistent.
 *
 * Only enforced on pin URLs. A plain viewport URL (`/@lat,lng,zoom`) is not a
 * pin, so navigating there must go to those coordinates, not snap to the pin.
 */
export const useEnforceLockedPinUrl = (): void => {
  const lockedPin = useRecoilValue(lockedMapPinAtom);
  const { latitudeParam, longitudeParam } = useLatLngFromUrl();
  const { setSelectedMapPin } = useSetMapPin();
  const isMapPinActive = useIsMapPinActive();

  useEffect(() => {
    if (!isMapPinActive || !lockedPin || !latitudeParam || !longitudeParam) {
      return;
    }
    // Compare at display precision so the redirected (rounded) URL doesn't
    // register as "different" and loop.
    const digits = COORDINATE_DISPLAY_FRACTION_DIGITS;
    const alreadyAtLockedPin =
      latitudeParam.toFixed(digits) === lockedPin.lat.toFixed(digits) &&
      longitudeParam.toFixed(digits) === lockedPin.lng.toFixed(digits);
    if (alreadyAtLockedPin) {
      return;
    }
    setSelectedMapPin(lockedPin, { replace: true });
  }, [
    isMapPinActive,
    lockedPin,
    latitudeParam,
    longitudeParam,
    setSelectedMapPin,
  ]);
};
